/**
 * Renders the name of an asset model based on its type and ID.
 * Unless isDiscovered is true (indicates the model passed in is already something like DeviceModel etc),
 * this will attempt to get the 'native' model so we can display the name as shown in its inventory view.
 */
import React from 'react';
import { inject, observer } from 'mobx-react';
import { IReactComponent } from 'mobx-react/dist/types/IReactComponent';
import { AssetType } from '@kentik/ui-shared/labels/lib';
import { ModelBase } from 'core/model/model.types';
import { AssetTagsStore } from 'app/$store.types';
import AgentRenderer from 'app/views/synthetics/components/AgentRenderer';
import CredentialNameRenderer from 'app/components/admin/renderers/CredentialNameRenderer';
import DeviceNameRenderer from 'app/components/admin/renderers/DeviceNameRenderer';
import LibraryNameRenderer from 'app/components/admin/renderers/LibraryNameRenderer';
import { policyNameRenderer } from 'app/views/alerting/policies/commonRenderers';
import TenantNameRenderer from 'app/components/admin/renderers/TenantNameRenderer';
import SynthTestNameRenderer from 'app/components/admin/renderers/SynthTestNameRenderer';

export type AssetModel = ModelBase & {
  get: (key: string) => string;
  isSelected: boolean;
  isSelectable: boolean;
};

interface Props {
  $assetTags: AssetTagsStore;
  assetType: AssetType;
  model: AssetModel;
  value: string | number;
  showLabels?: boolean;
  isDiscovered?: boolean;
}

function AssetModelRenderer(props: Props) {
  const { $assetTags, assetType, model, value, showLabels = false, isDiscovered = false } = props;
  const assetCollectionModel = isDiscovered
    ? model
    : $assetTags.getAssetModel({ assetType, assetId: model.get('asset_id') });

  if (!assetCollectionModel) {
    return value;
  }

  switch (assetType) {
    case AssetType.DEVICE:
      // @ts-expect-error wants DeviceModel
      return <DeviceNameRenderer model={assetCollectionModel} showLabels={showLabels} />;
    case AssetType.SYNTH_TEST:
      // @ts-expect-error wants TestModel
      return <SynthTestNameRenderer model={assetCollectionModel} showLabels={showLabels} />;
    case AssetType.SYNTH_AGENT:
      // @ts-expect-error wants locationInfo
      return <AgentRenderer agent={assetCollectionModel} showLabels={showLabels} />;
    case AssetType.CREDENTIAL_VAULT:
      // @ts-expect-error wants CredentialModel
      return <CredentialNameRenderer model={assetCollectionModel} value={value as string} />;
    case AssetType.DASHBOARD:
    case AssetType.BOARD:
    case AssetType.SAVED_VIEW:
      // @ts-expect-error wants BoardModel | DashboardModel | SavedView
      return <LibraryNameRenderer model={assetCollectionModel} showParametric={false} />;
    case AssetType.POLICY:
      return policyNameRenderer({ model: assetCollectionModel });
    case AssetType.TENANT:
      // @ts-expect-error wants $auth
      return <TenantNameRenderer model={assetCollectionModel} value={value} />;
    default:
      return value;
  }
}

export default inject('$assetTags')(observer(AssetModelRenderer as unknown as IReactComponent));
